import type React from "react"
import { useState } from "react"
import { Popover, Box, Typography, Button, Stack, CircularProgress } from "@mui/material"

interface StatusCellPopoverProps {
  status: string
  icon: React.ReactNode
  title?: string
  onOpen?: () => Promise<
    {
      label: string
      value: string
    }[]
  >
  errorMessage?: string | null
}

export function StatusCellPopover({ status, icon, title = "Status Details", onOpen, errorMessage }: StatusCellPopoverProps) {
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null)
  const [loading, setLoading] = useState(false)
  const [details, setDetails] = useState<{ label: string; value: string }[]>([])
  const [error, setError] = useState<string | null>(null)

  const handleClick = async (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget)

    if (onOpen) {
      setLoading(true)
      if(setError) setError(null)
      try {
        const data = await onOpen()
        setDetails(data)
      } catch (err) {
        if(errorMessage){
        setError(errorMessage);
        }else{
        setError(err instanceof Error ? err.message : "Failed to load details")
        }
        console.error("[v0] Error fetching popover data:", err)
      } finally {
        setLoading(false)
      }
    }
  }

  const handleClose = () => {
    setAnchorEl(null)
    setDetails([])
    setError(null)
  }

  const open = Boolean(anchorEl)

  return (
    <>
      <Button
        onClick={handleClick}
        sx={{
          minWidth: "auto",
          padding: 0,
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          cursor: "pointer",
          "&:hover": {
            opacity: 0.8,
          },
          transition: "opacity 0.2s",
        }}
        aria-label={`Open details for ${status}`}
      >
        {icon}
      </Button>

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "center",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "center",
        }}
      >
        <Box
          sx={{
            width: 320,
            padding: 2,
          }}
        >
          {/* Header */}
          <Box sx={{ paddingBottom: 1.5, borderBottom: "1px solid #e0e0e0" }}>
            <Typography
              variant="subtitle2"
              sx={{
                fontWeight: 600,
                fontSize: "14px",
                color: "#10383D",
              }}
            >
              {title}
            </Typography>
            <Typography
              variant="caption"
              sx={{
                marginTop: 0.5,
                display: "block",
                fontSize: "12px",
                color: "#666666",
              }}
            >
              {status}
            </Typography>
          </Box>

          {/* Loading State */}
          {loading && (
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                minHeight: "100px",
              }}
            >
              <CircularProgress size={24} />
            </Box>
          )}

          {/* Error State */}
          {error && !loading && (
            <Box sx={{ marginTop: 2, marginBottom: 2 }}>
              <Typography
                variant="caption"
                sx={{
                  fontSize: "12px",
                  color: "#d32f2f",
                }}
              >
                {error}
              </Typography>
            </Box>
          )}

          {/* Details */}
          {!loading && details.length > 0 && (
            <Stack spacing={1.5} sx={{ marginTop: 2, marginBottom: 2 }}>
              {details.map((detail, index) => (
                <Box
                  key={index}
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "flex-start",
                    gap: 1,
                  }}
                >
                  <Typography
                    variant="caption"
                    sx={{
                      fontSize: "12px",
                      fontWeight: 500,
                      color: "#666666",
                    }}
                  >
                    {detail.label}
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={{
                      fontSize: "12px",
                      fontWeight: 500,
                      color: "#10383D",
                      textAlign: "right",
                    }}
                  >
                    {detail.value}
                  </Typography>
                </Box>
              ))}
            </Stack>
          )}

          {/* Footer */}
          <Box sx={{ borderTop: "1px solid #e0e0e0", paddingTop: 1.5 }}>
            <Button
              onClick={handleClose}
              fullWidth
              variant="outlined"
              size="small"
              sx={{
                fontSize: "12px",
                fontWeight: 500,
                textTransform: "none",
                color: "#10383D",
                borderColor: "primary",
                "&:hover": {
                  backgroundColor: "#f5f5f5",
                  borderColor: "#d0d0d0",
                },
              }}
            >
              Close
            </Button>
          </Box>
        </Box>
      </Popover>
    </>
  )
}